import moment from 'moment-timezone';

/**
 * The single source of every date/time format in the app.
 *
 * - Showing a date or time to a person → pick a DATE_STYLES value and use
 *   getMomentFormat / formatMoment (or getPickerFormat for react-datepicker,
 *   which you get for free through components/AppDatePicker).
 * - Sending/parsing a value for the API → DATE_FORMATS.
 * Never pass a format string literal to `.format()` anywhere else.
 */

/**
 * Wire/storage formats. These describe how dates travel to and from the API and
 * NEVER follow the account's display format — e.g. a date-only screener answer is
 * always submitted as `MM/DD/YYYY` because the server parses it with `strtotime`.
 * For anything a person reads or types, use the display styles below.
 */
export const DATE_FORMATS = {
    /** Date only format: MM/DD/YYYY (e.g., "06/15/2025") */
    DATE_ONLY: 'MM/DD/YYYY',
    /** Time only format: hh:mm A (e.g., "02:30 PM") */
    TIME_ONLY: 'hh:mm A',
    /** Date and time format: MM/DD/YYYY hh:mm A (e.g., "06/15/2025 02:30 PM") */
    DATE_TIME: 'MM/DD/YYYY hh:mm A',
    /** ISO date format for API requests: YYYY-MM-DD (e.g., "2025-06-15") */
    API_DATE: 'YYYY-MM-DD',
    /** Naive datetime as the API stores it: YYYY-MM-DD HH:mm:ss */
    API_DATE_TIME: 'YYYY-MM-DD HH:mm:ss',
    /** Naive datetime to the minute: YYYY-MM-DD HH:mm */
    API_DATE_TIME_MINUTES: 'YYYY-MM-DD HH:mm',
    /** Value of an <input type="datetime-local">: YYYY-MM-DDTHH:mm */
    INPUT_DATE_TIME_LOCAL: 'YYYY-MM-DDTHH:mm',
    /** 24h clock time for API requests: HH:mm */
    API_TIME: 'HH:mm',
    /** Unix timestamp in seconds, as a string */
    UNIX_SECONDS: 'X'
};

/**
 * Display date formats an account can choose from. Adding a format is one entry
 * here — components never branch on the key, they ask for a named style.
 *
 * - `fieldOrder` / `separator` drive numeric dates (`09/30/2026`), the typed
 *   date pickers and the segment order of the participant `DateInput`.
 * - `monthNameDate` / `monthNameDayMonth` are the moment patterns for dates with
 *   a month name, so their day/month order mirrors the numeric one.
 */
export const DATE_FORMAT_REGISTRY = Object.freeze({
    'MM/DD/YYYY': {
        fieldOrder: ['month', 'day', 'year'],
        separator: '/',
        monthNameDate: 'MMM D, YYYY',
        monthNameDayMonth: 'MMM D'
    },
    'DD/MM/YYYY': {
        fieldOrder: ['day', 'month', 'year'],
        separator: '/',
        monthNameDate: 'D MMM YYYY',
        monthNameDayMonth: 'D MMM'
    }
});

export const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';

/**
 * Named display styles. Call sites pick one of these; the pattern behind it comes
 * from the active registry entry.
 */
export const DATE_STYLES = Object.freeze({
    /** 09/30/2026 */
    DATE: 'DATE',
    /** 09/30/2026 11:14 AM */
    DATE_TIME: 'DATE_TIME',
    /** Sep 30, 2026 */
    HUMAN_DATE: 'HUMAN_DATE',
    /** Sep 30, 2026 at 11:14 AM */
    HUMAN_DATE_TIME: 'HUMAN_DATE_TIME',
    /** Sep 30, 2026 at 11:14 AM (BST) */
    HUMAN_DATE_TIME_TZ: 'HUMAN_DATE_TIME_TZ',
    /** Sep 30 */
    HUMAN_DAY_MONTH: 'HUMAN_DAY_MONTH',
    /** Sep 30 at 11:14 AM */
    HUMAN_DAY_MONTH_TIME: 'HUMAN_DAY_MONTH_TIME',
    /** Sep 30 at 11:14 AM (BST) */
    HUMAN_DAY_MONTH_TIME_TZ: 'HUMAN_DAY_MONTH_TIME_TZ',
    /** Wednesday, Sep 30, 2026 */
    HUMAN_WEEKDAY_DATE: 'HUMAN_WEEKDAY_DATE',
    /** Wednesday, Sep 30, 2026 at 11:14 AM */
    HUMAN_WEEKDAY_DATE_TIME: 'HUMAN_WEEKDAY_DATE_TIME',
    /** Sep 30, 2026 at 11:14:09 AM — when two values can fall in the same minute (versions, audit timestamps) */
    HUMAN_DATE_TIME_SECONDS: 'HUMAN_DATE_TIME_SECONDS',
    /** Sep 30, 2026 at 11:14:09 AM (BST) */
    HUMAN_DATE_TIME_SECONDS_TZ: 'HUMAN_DATE_TIME_SECONDS_TZ',
    /** 11:14 AM */
    TIME: 'TIME',
    /** 11:14 AM BST */
    TIME_TZ: 'TIME_TZ',
    /** BST */
    TIMEZONE: 'TIMEZONE',
    /** Wednesday */
    WEEKDAY: 'WEEKDAY',
    /** Wed */
    WEEKDAY_SHORT: 'WEEKDAY_SHORT',
    /** Sep */
    MONTH_SHORT: 'MONTH_SHORT',
    /** 30 */
    DAY_OF_MONTH: 'DAY_OF_MONTH',
    /** Sep 2026 */
    MONTH_YEAR: 'MONTH_YEAR'
});

// One clock format for every display style; react-datepicker needs the date-fns spelling.
const TIME = 'h:mm A';
const TIME_SECONDS = 'h:mm:ss A';
const PICKER_TIME = 'h:mm aa';

const MOMENT_FIELD_TOKENS = { month: 'MM', day: 'DD', year: 'YYYY' };
const DATE_FNS_FIELD_TOKENS = { month: 'MM', day: 'dd', year: 'yyyy' };

let activeDateFormat = DEFAULT_DATE_FORMAT;

/**
 * Sets the display format used when a call site doesn't pass one explicitly.
 * Unknown keys fall back to the default so a stale setting can't break rendering.
 *
 * Nothing subscribes to this value: components read it during render. Call it
 * synchronously before the first render that shows a date (app bootstrap from
 * the stored account, or a public page before it renders its form), not from a
 * `useEffect` after a query resolves — otherwise already-painted dates keep the
 * old format while later renders use the new one. Memoised values built from a
 * format must list that format in their deps. See ADR-012.
 *
 * @param {string} [formatKey] - A key of DATE_FORMAT_REGISTRY
 */
export const setActiveDateFormat = formatKey => {
    activeDateFormat = DATE_FORMAT_REGISTRY[formatKey] ? formatKey : DEFAULT_DATE_FORMAT;
};

/** @returns {string} The registry key currently used for display */
export const getActiveDateFormat = () => activeDateFormat;

/**
 * @param {string} [formatKey] - A key of DATE_FORMAT_REGISTRY; defaults to the active one
 * @returns {{fieldOrder: string[], separator: string, monthNameDate: string, monthNameDayMonth: string}}
 */
export const getDateFormatConfig = (formatKey = activeDateFormat) =>
    DATE_FORMAT_REGISTRY[formatKey] || DATE_FORMAT_REGISTRY[DEFAULT_DATE_FORMAT];

const numericPattern = (config, tokens) => config.fieldOrder.map(field => tokens[field]).join(config.separator);

/**
 * Moment.js pattern for a display style.
 *
 * @param {string} style - One of DATE_STYLES
 * @param {string} [formatKey] - A key of DATE_FORMAT_REGISTRY; defaults to the active one
 * @returns {string} Moment format string
 */
export const getMomentFormat = (style, formatKey) => {
    const config = getDateFormatConfig(formatKey);
    const date = numericPattern(config, MOMENT_FIELD_TOKENS);
    const { monthNameDate, monthNameDayMonth } = config;

    switch (style) {
        case DATE_STYLES.DATE:
            return date;
        case DATE_STYLES.DATE_TIME:
            return `${date} ${TIME}`;
        case DATE_STYLES.HUMAN_DATE:
            return monthNameDate;
        case DATE_STYLES.HUMAN_DATE_TIME:
            return `${monthNameDate} [at] ${TIME}`;
        case DATE_STYLES.HUMAN_DATE_TIME_TZ:
            return `${monthNameDate} [at] ${TIME} (z)`;
        case DATE_STYLES.HUMAN_DAY_MONTH:
            return monthNameDayMonth;
        case DATE_STYLES.HUMAN_DAY_MONTH_TIME:
            return `${monthNameDayMonth} [at] ${TIME}`;
        case DATE_STYLES.HUMAN_DAY_MONTH_TIME_TZ:
            return `${monthNameDayMonth} [at] ${TIME} (z)`;
        case DATE_STYLES.HUMAN_WEEKDAY_DATE:
            return `dddd, ${monthNameDate}`;
        case DATE_STYLES.HUMAN_WEEKDAY_DATE_TIME:
            return `dddd, ${monthNameDate} [at] ${TIME}`;
        case DATE_STYLES.HUMAN_DATE_TIME_SECONDS:
            return `${monthNameDate} [at] ${TIME_SECONDS}`;
        case DATE_STYLES.HUMAN_DATE_TIME_SECONDS_TZ:
            return `${monthNameDate} [at] ${TIME_SECONDS} (z)`;
        case DATE_STYLES.TIME:
            return TIME;
        case DATE_STYLES.TIME_TZ:
            return `${TIME} z`;
        case DATE_STYLES.TIMEZONE:
            return 'z';
        case DATE_STYLES.WEEKDAY:
            return 'dddd';
        case DATE_STYLES.WEEKDAY_SHORT:
            return 'ddd';
        case DATE_STYLES.MONTH_SHORT:
            return 'MMM';
        case DATE_STYLES.DAY_OF_MONTH:
            return 'D';
        case DATE_STYLES.MONTH_YEAR:
            return 'MMM YYYY';
        default:
            throw new Error(`Unknown date style: ${style}`);
    }
};

/**
 * date-fns pattern for react-datepicker's `dateFormat`, which drives both what the
 * input shows and how typed text is parsed. Only numeric styles make sense here.
 *
 * @param {string} style - DATE_STYLES.DATE, DATE_STYLES.DATE_TIME or DATE_STYLES.TIME
 * @param {string} [formatKey] - A key of DATE_FORMAT_REGISTRY; defaults to the active one
 * @returns {string} date-fns format string
 */
export const getPickerFormat = (style, formatKey) => {
    const date = numericPattern(getDateFormatConfig(formatKey), DATE_FNS_FIELD_TOKENS);

    switch (style) {
        case DATE_STYLES.DATE:
            return date;
        case DATE_STYLES.DATE_TIME:
            return `${date} ${PICKER_TIME}`;
        case DATE_STYLES.TIME:
            return PICKER_TIME;
        default:
            throw new Error(`Unsupported date picker style: ${style}`);
    }
};

/**
 * Formats a moment in a display style. Parsing and timezone conversion stay with
 * the caller, because sources differ (UTC strings, study timezone, browser-local).
 *
 * @param {moment.Moment} momentValue - Parsed moment
 * @param {string} style - One of DATE_STYLES
 * @param {string} [formatKey] - A key of DATE_FORMAT_REGISTRY; defaults to the active one
 * @returns {string} Formatted date, or '' for an invalid moment
 */
export const formatMoment = (momentValue, style, formatKey) =>
    momentValue && momentValue.isValid() ? momentValue.format(getMomentFormat(style, formatKey)) : '';

/**
 * Returns the moment.js display format for a date-picker question.
 *
 * @param {boolean} showTimeSelectOnly - Whether to show only time (no date)
 * @param {boolean} showTimeSelect - Whether to show time along with date
 * @param {string} [formatKey] - A key of DATE_FORMAT_REGISTRY; defaults to the active one
 * @returns {string} Format string for moment.js
 *
 * @example
 * getDisplayFormat(false, false) // 'MM/DD/YYYY'
 * getDisplayFormat(true, false) // 'h:mm A'
 * getDisplayFormat(false, true) // 'MM/DD/YYYY h:mm A'
 */
const getDisplayFormat = (showTimeSelectOnly, showTimeSelect, formatKey) => {
    if (showTimeSelectOnly) {
        return getMomentFormat(DATE_STYLES.TIME, formatKey);
    } else if (showTimeSelect) {
        return getMomentFormat(DATE_STYLES.DATE_TIME, formatKey);
    }
    return getMomentFormat(DATE_STYLES.DATE, formatKey);
};

/**
 * Returns the format a date-picker answer is submitted in. Independent of the
 * display format — see DATE_FORMATS.
 *
 * @param {boolean} showTimeSelectOnly - Whether the question is time-only
 * @param {boolean} showTimeSelect - Whether the question includes a time
 * @returns {string} Format string for moment.js
 */
export const getStorageFormat = (showTimeSelectOnly, showTimeSelect) => {
    if (showTimeSelectOnly) {
        return DATE_FORMATS.TIME_ONLY;
    } else if (showTimeSelect) {
        return DATE_FORMATS.DATE_TIME;
    }
    return DATE_FORMATS.DATE_ONLY;
};

/**
 * Whether a date-picker question represents an absolute instant (date + time) —
 * the only shape where a researcher's profile timezone applies. Date-only and
 * time-only answers are TZ-agnostic: shifting them by an offset would change
 * the calendar day or clock hour the participant picked.
 *
 * @param {boolean} showTimeSelectOnly - Question is time-only
 * @param {boolean} showTimeSelect - Question includes a time component
 * @returns {boolean} true when the value is a TZ-aware datetime
 */
export const isDateTimeQuestion = (showTimeSelectOnly, showTimeSelect) =>
    Boolean(showTimeSelect && !showTimeSelectOnly);

/**
 * Formats a stored date-picker answer for display.
 *
 * Datetime answers (`showTimeSelect=true`) are an absolute instant. `DateInput`
 * submits them as ISO 8601 UTC (which `moment(...)` parses natively); older
 * legacy answers were submitted as naive `"MM/DD/YYYY hh:mm A"` browser-local
 * strings. `moment(value)` handles both: it recognizes the ISO `Z` and uses
 * the absolute moment, and for the legacy local string it falls back to
 * browser-local parsing (matching how `StudyParticipantsList`'s screener-answer
 * column already renders the same value via `new Date(value)`). We then
 * `.tz(timezone)` to localize to the researcher's profile TZ — keeping read
 * mode aligned with the study table and `Answered At`.
 *
 * Date-only and time-only answers are intentionally TZ-agnostic: shifting them
 * by the profile offset would change the calendar day or clock time the
 * participant picked. When no timezone is supplied (participant view) we just
 * format in browser-local so nothing about the participant flow changes.
 *
 * @param {string|Date|undefined} value - Stored answer value
 * @param {Object} [options]
 * @param {string} [options.timezone] - IANA timezone (researcher's profile TZ)
 * @param {boolean} [options.showTimeSelectOnly] - Whether the question is time-only
 * @param {boolean} [options.showTimeSelect] - Whether the question includes time
 * @returns {string} Formatted value or '' when value is falsy/invalid
 */
export const formatDatePickerAnswer = (value, { timezone, showTimeSelectOnly, showTimeSelect } = {}) => {
    if (!value) return '';

    const format = getDisplayFormat(showTimeSelectOnly, showTimeSelect);
    const isDateTime = isDateTimeQuestion(showTimeSelectOnly, showTimeSelect);

    let parsed = timezone && isDateTime ? moment(value).tz(timezone) : moment(value);
    // Time-only answers like "11:11 AM" aren't recognized by moment's default heuristics.
    if (!parsed.isValid() && showTimeSelectOnly) {
        parsed = moment(value, DATE_FORMATS.TIME_ONLY);
    }

    return parsed.isValid() ? parsed.format(format) : '';
};
